// Initial Meeting Documents — a dedicated list, separate from Quotes,
// because these are more like discussion summaries than commercial quotes.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { EmptyState, Pill, Spinner } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Plus, FileText, Search } from "lucide-react";
import { formatDate } from "../lib/dates";

export default function InitialDocs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.get("/quotes?quote_type=initial_meeting"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const dateStr = it.created_at ? String(it.created_at).slice(0, 10) : "";
      const dateDisplay = formatDate(dateStr).toLowerCase();
      return (
        (it.customer_name || "").toLowerCase().includes(q) ||
        (it.brand_name || "").toLowerCase().includes(q) ||
        (it.quote_number || "").toLowerCase().includes(q) ||
        (it.data?.category || "").toLowerCase().includes(q) ||
        dateStr.includes(q) || dateDisplay.includes(q)
      );
    });
  }, [items, query]);

  const tone = (st) =>
    st === "sent" || st === "approved" || st === "paid" ? "success"
      : st === "rejected" ? "danger" : "neutral";

  return (
    <>
      <AppHeader title="Initial Meeting Docs" back />
      <div className="sg-main sg-fade-in">
        <div className="sg-card" style={{ marginBottom: 14, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--surface-subtle)", display: "grid", placeItems: "center", flexShrink: 0 }}>
            <FileText size={18} color="var(--brand)" />
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.4 }}>
            Discussion summaries you share with clients after the initial meeting — indicative pricing, MOQs and timelines. Not a formal quote.
          </div>
        </div>

        <div style={{ position: "relative", marginBottom: 14 }}>
          <Search size={16} color="var(--muted)" style={{ position: "absolute", left: 14, top: 15 }} />
          <input
            className="sg-input"
            placeholder="Search by customer, number or date"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 40 }}
            data-testid="initial-docs-search"
          />
        </div>

        {loading ? <Spinner /> : filtered.length === 0 ? (
          <EmptyState
            title={query ? `No matches for "${query}"` : "No initial docs yet"}
            hint={query ? "Try a different name, number or date." : "Tap + to draft one after your first client meeting."}
          />
        ) : (
          filtered.map((q) => (
            <div key={q.quote_id} className="sg-list-card" onClick={() => navigate(`/quotes/${q.quote_id}`)} data-testid={`initial-doc-row-${q.quote_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "var(--brand)", letterSpacing: 0.4 }}>{q.quote_number}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.customer_name}</div>
                  {q.created_at && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{formatDate(String(q.created_at).slice(0, 10))}</div>}
                </div>
                <Pill label={q.status} tone={tone(q.status)} />
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                {q.data?.category || "General"}
              </div>
            </div>
          ))
        )}
      </div>

      <button
        className="sg-fab"
        onClick={() => navigate("/quotes/new?type=initial_meeting")}
        data-testid="initial-docs-add-button"
        aria-label="New initial doc"
      >
        <Plus size={24} />
      </button>
    </>
  );
}
